import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearResetRequired, logout } from '../store/slices/authSlice';
import { apiFetch, peekToken, resetSession } from '../utils/streamApi';
import { unregisterPushOnLogout } from '../utils/pushNotifications';
import { useTheme } from '../contexts/ThemeContext';
import { useSize } from '../contexts/SizeContext';
import { useSidebar } from '../contexts/SidebarContext';
import useIsMobile from '../utils/useIsMobile';
import { useEntitlements } from '../utils/entitlements';
import Logo from './Logo';
import PanicModal from './PanicModal';
import '../css/navbar.css';

// Client ID: the value entered at login, else decoded from the JWT payload.
function getClientId() {
  const ls = localStorage.getItem('prvsk_client_id');
  if (ls) return ls;
  try {
    const t = peekToken();
    if (t) return JSON.parse(atob(t.split('.')[1])).client_id || '';
  } catch { /* ignore */ }
  return '';
}

// Live wall-clock for the top bar — date + seconds-ticking time.
function NavClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const pad = n => String(n).padStart(2, '0');
  return (
    <div className="nav-clock" title="Current date & time">
      <span className="nav-clock-time">{pad(now.getHours())}:{pad(now.getMinutes())}:{pad(now.getSeconds())}</span>
      <span className="nav-clock-date">{now.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}</span>
    </div>
  );
}

// Filename-prefix types that map to CRITICAL severity (mirror of the Ops Console mapping).
const CRITICAL_TYPES = new Set(['banned', 'fire', 'smoke', 'arms', 'weapon', 'gun', 'camera', 'tamper', 'zone', 'boundary']);

// Live critical-alert count for today → one click opens the Ops Console pre-filtered to critical.
function NavCritical() {
  const navigate = useNavigate();
  const { clientType } = useEntitlements();
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (clientType !== 'industrial') return;
    let alive = true;
    const load = async () => {
      try {
        const d = new Date();
        const day = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
        const r = await apiFetch(`/ui/alerts/summary?exclude=snapshot&date_from=${day}&date_to=${day}`).then(x => x.ok ? x.json() : null);
        if (!alive || !r || !r.by_type) return;
        let c = 0;
        Object.entries(r.by_type).forEach(([t, n]) => { if (CRITICAL_TYPES.has(String(t).toLowerCase())) c += n; });
        setCount(c);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [clientType]);
  // Badge opens the Ops Console, an industrial-only route — never show it on home clients.
  if (clientType !== 'industrial' || !count) return null;
  return (
    <button className="nav-crit" title={`${count} critical alert${count === 1 ? '' : 's'} today — open Ops Console`}
      onClick={() => navigate('/industrial?sev=critical')}>
      ⚠ <b>{count}</b>
    </button>
  );
}

const NO_BACK_PATHS = ['/dashboard', '/login', '/change-password'];

const PAGE_TITLES = {
  '/':             '📹 My Cameras',
  '/alerts':       '🚨 Alerts',
  '/recordings':   '📼 Recordings',
  '/faces/known':  '👤 Known Faces',
  '/faces/banned': '🚫 Banned',
  '/admin-config': '⚙️ Admin Config',
  '/schedule':     '📅 Schedule',
};

const BETA_PAGES = ['/faces/known', '/faces/banned'];

function ChangePasswordModal({ onClose }) {
  const dispatch = useDispatch();

  const [current,  setCurrent]  = useState('');
  const [next,     setNext]     = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [msg,      setMsg]      = useState(null);
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (next.length < 4) { setMsg({ type: 'error', text: 'New password must be at least 4 characters' }); return; }
    if (next !== confirm) { setMsg({ type: 'error', text: 'New passwords do not match' }); return; }
    setLoading(true);
    try {
      const res = await apiFetch('/auth/ui/change-password', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ current_password: current, new_password: next }),
      });
      if (!res.ok) {
        const d = await res.json();
        setMsg({ type: 'error', text: d.detail || 'Failed to change password' });
        return;
      }
      dispatch(clearResetRequired());
      setMsg({ type: 'success', text: 'Password changed successfully!' });
      setTimeout(onClose, 1200);
    } catch {
      setMsg({ type: 'error', text: 'Cannot reach server' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>🔑 Change Password</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="modal-field">
            <label>Current Password</label>
            <input className="modal-input" type="password" placeholder="Enter current password"
              value={current} onChange={e => setCurrent(e.target.value)} required autoFocus />
          </div>
          <div className="modal-field">
            <label>New Password</label>
            <input className="modal-input" type="password" placeholder="Enter new password"
              value={next} onChange={e => setNext(e.target.value)} required />
          </div>
          <div className="modal-field">
            <label>Confirm New Password</label>
            <input className="modal-input" type="password" placeholder="Confirm new password"
              value={confirm} onChange={e => setConfirm(e.target.value)} required />
          </div>
          {msg && <div className={`modal-msg ${msg.type}`}>{msg.text}</div>}
          <div className="modal-actions">
            <button type="button" className="modal-btn secondary" onClick={onClose} disabled={loading}>Cancel</button>
            <button type="submit" className="modal-btn primary" disabled={loading}>
              {loading ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Navbar() {
  const dispatch   = useDispatch();
  const location   = useLocation();
  const navigate   = useNavigate();
  const isMobile   = useIsMobile();
  const username   = useSelector(s => s.auth.username);
  const { themeKey, themes, switchTheme } = useTheme();
  const { sizeKey, sizes, switchSize } = useSize();
  const { mobileOpen, toggleMobile } = useSidebar();
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [showPanic, setShowPanic] = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const menuRef = useRef(null);
  const clientId = getClientId();

  // Close the profile dropdown on any outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  // resetSession() too: the token now outlives the tab (localStorage), so logout
  // has to drop it explicitly or a valid JWT is left behind in the browser.
  const doLogout = () => { setMenuOpen(false); unregisterPushOnLogout(); resetSession(); dispatch(logout()); navigate('/login', { replace: true }); };

  const showBack = isMobile
    && !NO_BACK_PATHS.includes(location.pathname)
    && window.history.length > 1;

  const pageTitle = Object.entries(PAGE_TITLES).find(([path]) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)
  )?.[1] ?? '';

  return (
    <>
      <nav className="navbar">

        {/* Back button — mobile only, on non-home pages */}
        {showBack && (
          <button className="navbar-back-btn" onClick={() => navigate(-1)} aria-label="Go back">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}

        {/* Hamburger — mobile only */}
        <button
          className={`navbar-hamburger${mobileOpen ? ' open' : ''}`}
          onClick={toggleMobile}
          aria-label="Menu"
        >
          {mobileOpen ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <line x1="4" y1="4" x2="16" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="16" y1="4" x2="4" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <line x1="3" y1="5"  x2="17" y2="5"  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="3" y1="15" x2="17" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
        </button>

        <div className="navbar-brand">
          <span className="navbar-logo-icon">
            <Logo />
          </span>
          <span className="navbar-brand-text">
            <span className="navbar-title">PARVEKSHAK</span>
            <span className="navbar-subtitle">AI Surveillance</span>
          </span>
        </div>

        {/* Client name/ID — top-left, right after the logo. Hidden for now
            (still shown in the profile dropdown). Flip to re-enable. */}
        {false && clientId && <span className="navbar-client" title="Client ID">{clientId}</span>}

        {pageTitle && (
          <>
            <div className="navbar-divider" />
            <span className="navbar-page-title">
              {pageTitle}
              {BETA_PAGES.some(p => location.pathname.startsWith(p)) && (
                <span className="navbar-beta">β</span>
              )}
            </span>
          </>
        )}

        <div className="navbar-spacer" />

        {/* Critical-alerts badge (today) → Ops Console critical; live clock */}
        <NavCritical />
        {!isMobile && <NavClock />}

        {/* Emergency SOS — always one click, top-right */}
        <button className="nav-sos" onClick={() => setShowPanic(true)} title="Send panic / SOS alert">
          <span>🆘</span>{!isMobile && <span>SOS</span>}
        </button>

        {/* User profile dropdown — layout, theme, client id, change password, logout */}
        <div className="nav-profile" ref={menuRef}>
          <button className={`nav-user-btn${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(o => !o)}
            title="Account &amp; settings" aria-haspopup="true" aria-expanded={menuOpen}>
            <span className="nav-user-avatar">👤</span>
            {!isMobile && <span className="nav-user-name">{username}</span>}
            <span className="nav-user-caret">▾</span>
          </button>

          {menuOpen && (
            <div className="nav-menu" role="menu">
              <div className="nav-menu-head">
                <div className="nav-menu-user">👤 {username || 'User'}</div>
                {clientId && <div className="nav-menu-client">Client&nbsp;ID: <b>{clientId}</b></div>}
              </div>

              <div className="nav-menu-row">
                <label>Layout</label>
                <select className="theme-select" value={sizeKey} onChange={e => switchSize(e.target.value)} title="UI layout / font size">
                  {sizes.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>

              <div className="nav-menu-row">
                <label>Theme</label>
                <select className="theme-select" value={themeKey} onChange={e => switchTheme(e.target.value)} title="Switch theme">
                  {themes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>

              <button className="nav-menu-item" onClick={() => { setMenuOpen(false); setShowChangePwd(true); }}>
                <span>🔑</span> Change Password
              </button>
              <button className="nav-menu-item danger" onClick={doLogout}>
                <span>⏻</span> Logout
              </button>
            </div>
          )}
        </div>

      </nav>

      {showChangePwd && <ChangePasswordModal onClose={() => setShowChangePwd(false)} />}
      {showPanic && <PanicModal onClose={() => setShowPanic(false)} />}
    </>
  );
}
